import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { friendlyLabel, friendlyOptionLabel } from '@shared/utils/friendlyLabels';
import { displayNameForIdentity } from '@shared/utils/privacy';
import { statusPillClassName } from '@shared/utils/statusPill';
import type { ClientWorkflowState, JobsResponse, ReportJobRecord } from '@shared/types/api';
import { useClientAccount } from '../account/AccountContext';
import PackageGenerationBanner from '../components/PackageGenerationBanner';
import { CMMC_LEVEL_OPTIONS, DEFAULT_GRID_LIMIT, GRID_LIMIT_OPTIONS, metadataFromResponse, type GridMetadata, type SearchMode } from './gridUi';
type ApprovalActionStatus = {
  kind: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
};

type EvidenceWorkflowAccess = {
  clientId?: string;
  accountId?: string;
  scopeKey?: string;
  featureKey?: string;
  enabled: boolean;
  canRequest: boolean;
  accountRole?: string | null;
  approvalRequired?: boolean;
  requestedAction?: string;
  workflowType?: string;
  workflowActive?: boolean;
  workflowState?: ClientWorkflowState;
  message?: string;
};

type EvidenceWorkflowRequestResponse = EvidenceWorkflowAccess & {
  batchId?: string;
  jobId?: string;
  requestedAt?: string;
  requestedBy?: string;
  message?: string;
};


type ApprovalDecisionLabelSet = {
  gridApprove: string;
  detailApprove: string;
  decline: string;
  approveTitle: string;
  declineTitle: string;
  approving: string;
  declining: string;
  approved: string;
  declined: string;
  approveConfirm: string;
  declineConfirm: string;
};

function logicalApprovalJobId(value: string | undefined): string {
  const raw = String(value ?? '').trim();
  let candidate = raw;
  try {
    candidate = decodeURIComponent(raw);
  } catch {
    candidate = raw;
  }
  const match = candidate.match(/^AWS#\d{12}#APPROVAL#(.+)$/);
  return match?.[1]?.trim() || candidate;
}


const DEFAULT_APPROVAL_DECISION_LABELS: ApprovalDecisionLabelSet = {
  gridApprove: 'Approve',
  detailApprove: 'Approve request',
  decline: 'Decline',
  approveTitle: 'Approve this request',
  declineTitle: 'Decline this request',
  approving: 'Submitting approval...',
  declining: 'Submitting decline...',
  approved: 'Approval recorded.',
  declined: 'Decline recorded.',
  approveConfirm: 'Approve this request?',
  declineConfirm: 'Decline this request?',
};

const APPROVAL_DECISION_LABEL_MAPPINGS: Array<{ pattern: RegExp; labels: ApprovalDecisionLabelSet }> = [
  {
    pattern: /run[_\s-]*evidence[_\s-]*(package|only)|collect[_\s-]*evidence|evidence[_\s-]*(only|package|collection)/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Approve',
      detailApprove: 'Approve evidence package',
      approveTitle: 'Approve evidence package generation',
      approving: 'Submitting collection approval...',
      approved: 'Evidence package generation approved.',
      declined: 'Evidence package generation declined.',
      approveConfirm: 'Approve evidence package generation for this client?',
      declineConfirm: 'Decline evidence package generation for this client?',
    },
  },
  {
    pattern: /run[_\s-]*evidence[_\s-]*workflow|run[_\s-]*technical[_\s-]*baseline|full[_\s-]*(workflow|readiness)|technical[_\s-]*baseline/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Approve all',
      detailApprove: 'Approve full workflow',
      approveTitle: 'Approve software update and evidence package generation',
      approving: 'Submitting full workflow approval...',
      approved: 'Full workflow approved.',
      declined: 'Full workflow declined.',
      approveConfirm: 'Approve the full workflow for this client? This allows software deployment first, then evidence package generation after deployment succeeds.',
      declineConfirm: 'Decline the full workflow for this client?',
    },
  },
  {
    pattern: /run[_\s-]*(cdk[_\s-]*deployment|client[_\s-]*baseline[_\s-]*deploy|deploy[_\s-]*client[_\s-]*baseline)|cdk[_\s-]*deploy|software[_\s-]*update|deploy[_\s-]*platform|platform[_\s-]*update/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Deploy',
      detailApprove: 'Approve deployment',
      approveTitle: 'Approve platform update deployment',
      approving: 'Submitting deployment approval...',
      approved: 'Platform deployment approved.',
      declined: 'Platform deployment declined.',
      approveConfirm: 'Approve platform update deployment for this client?',
      declineConfirm: 'Decline platform update deployment for this client?',
    },
  },
  {
    pattern: /full[_\s-]*readiness|readiness[_\s-]*refresh/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Refresh',
      detailApprove: 'Approve refresh',
      approveTitle: 'Approve full readiness refresh',
      approving: 'Submitting refresh approval...',
      approved: 'Full readiness refresh approved.',
      declined: 'Full readiness refresh declined.',
      approveConfirm: 'Approve full readiness refresh for this client?',
      declineConfirm: 'Decline full readiness refresh for this client?',
    },
  },
  {
    pattern: /remediation/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Remediate',
      detailApprove: 'Approve remediation',
      approveTitle: 'Approve remediation deployment',
      approving: 'Submitting remediation approval...',
      approved: 'Remediation approved.',
      declined: 'Remediation declined.',
      approveConfirm: 'Approve remediation deployment for this client?',
      declineConfirm: 'Decline remediation deployment for this client?',
    },
  },
  {
    pattern: /workspaces|enclave/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Deploy',
      detailApprove: 'Approve enclave',
      approveTitle: 'Approve WorkSpaces enclave deployment',
      approving: 'Submitting enclave approval...',
      approved: 'WorkSpaces enclave deployment approved.',
      declined: 'WorkSpaces enclave deployment declined.',
      approveConfirm: 'Approve WorkSpaces enclave deployment for this client?',
      declineConfirm: 'Decline WorkSpaces enclave deployment for this client?',
    },
  },
  {
    pattern: /final[_\s-]*(auditor|package)|auditor[_\s-]*package|package[_\s-]*release/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Release',
      detailApprove: 'Approve release',
      approveTitle: 'Approve final package release',
      approving: 'Submitting release approval...',
      approved: 'Final package release approved.',
      declined: 'Final package release declined.',
      approveConfirm: 'Approve final auditor package release?',
      declineConfirm: 'Decline final auditor package release?',
    },
  },
  {
    pattern: /client[_\s-]*evidence[_\s-]*submission|evidence[_\s-]*submission/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Submit',
      detailApprove: 'Approve submission',
      approveTitle: 'Approve client evidence submission',
      approving: 'Submitting evidence submission approval...',
      approved: 'Client evidence submission approved.',
      declined: 'Client evidence submission declined.',
      approveConfirm: 'Approve client evidence submission?',
      declineConfirm: 'Decline client evidence submission?',
    },
  },
  {
    pattern: /teardown|delete|destroy/i,
    labels: {
      ...DEFAULT_APPROVAL_DECISION_LABELS,
      gridApprove: 'Teardown',
      detailApprove: 'Confirm teardown',
      decline: 'Cancel',
      approveTitle: 'Confirm environment teardown',
      declineTitle: 'Cancel teardown request',
      approving: 'Submitting teardown approval...',
      declining: 'Cancelling teardown request...',
      approved: 'Environment teardown approved.',
      declined: 'Environment teardown cancelled.',
      approveConfirm: 'Confirm teardown approval for this client environment?',
      declineConfirm: 'Cancel this teardown request?',
    },
  },
];

interface ApprovalSearchState {
  search: string;
  searchMode: SearchMode;
  status: string;
  cmmcLevel: string;
  limit: number;
}

const STATUS_OPTIONS = [
  '',
  'PENDING',
  'CLIENT_APPROVAL_REQUESTED',
  'PENDING_CLIENT_APPROVAL',
  'CLIENT_APPROVED_EVIDENCE_GENERATION',
  'CLIENT_DECLINED_EVIDENCE_GENERATION',
  'CLIENT_DENIED_EVIDENCE_GENERATION',
  'SUPERSEDED',
  'ORCHESTRATION_STARTED',
  'ORCHESTRATION_WORKER_SUCCEEDED',
  'ORCHESTRATION_FAILED',
];

function formatDate(value?: string): string {
  return formatLocalDateTime(value);
}

function valueOrDash(value?: string | number | null): string {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

function jobString(job: ReportJobRecord, field: string): string {
  const value = (job as unknown as Record<string, unknown>)[field];
  if (value === undefined || value === null || value === '') return '';
  return String(value);
}

function friendlyStatus(value: string): string {
  return friendlyLabel(value || '—');
}

type ApprovalWorkflowKind = 'full' | 'software' | 'evidence' | 'other';

function approvalStatusLabel(kind: ApprovalWorkflowKind, rawStatus: string): string {
  const normalized = rawStatus.trim().toUpperCase();

  if (['PENDING', 'PENDING_CLIENT_APPROVAL', 'CLIENT_APPROVAL_REQUESTED'].includes(normalized)) {
    if (kind === 'full') return 'Pending update & evidence';
    if (kind === 'software') return 'Pending software update';
    if (kind === 'evidence') return 'Pending evidence package';
    return 'Pending approval';
  }

  if (normalized === 'CLIENT_APPROVED_EVIDENCE_GENERATION' || normalized.includes('APPROVED')) {
    if (kind === 'full') return 'Approved update & evidence';
    if (kind === 'software') return 'Approved software update';
    if (kind === 'evidence') return 'Approved evidence package';
    return 'Approved';
  }

  if (normalized === 'CLIENT_DECLINED_EVIDENCE_GENERATION' || normalized.includes('DECLINED') || normalized === 'CLIENT_DENIED_EVIDENCE_GENERATION' || normalized.includes('DENIED') || normalized.includes('REJECTED')) {
    if (kind === 'full') return 'Declined update & evidence';
    if (kind === 'software') return 'Declined software update';
    if (kind === 'evidence') return 'Declined evidence package';
    return 'Declined';
  }

  if (normalized === 'ORCHESTRATION_STARTED') {
    if (kind === 'full') return 'Running full workflow';
    if (kind === 'software') return 'Deploying update';
    if (kind === 'evidence') return 'Building evidence package';
    return 'Running';
  }

  if (normalized === 'ORCHESTRATION_WORKER_SUCCEEDED') {
    if (kind === 'full') return 'Workflow completed';
    if (kind === 'software') return 'Software update completed';
    if (kind === 'evidence') return 'Evidence package completed';
    return 'Completed';
  }

  if (normalized === 'ORCHESTRATION_FAILED' || normalized.includes('FAILED') || normalized.includes('ERROR')) {
    if (kind === 'full') return 'Workflow failed';
    if (kind === 'software') return 'Software update failed';
    if (kind === 'evidence') return 'Evidence package failed';
    return 'Failed';
  }

  return friendlyStatus(normalized || 'Pending approval');
}

function approvalStatusPillClassName(job: ReportJobRecord): string {
  const rawStatus = rawApprovalStatus(job);
  const kind = approvalWorkflowKind(job);
  const normalized = rawStatus.trim().toUpperCase();
  const baseClassName = statusPillClassName(normalized);

  if (normalized === 'CLIENT_APPROVED_EVIDENCE_GENERATION' || normalized.includes('APPROVED')) {
    return `${baseClassName} approval-status-pill-${kind}`;
  }

  if (['PENDING', 'PENDING_CLIENT_APPROVAL', 'CLIENT_APPROVAL_REQUESTED'].includes(normalized)) {
    return `${baseClassName} approval-status-pill-pending approval-status-pill-${kind}`;
  }

  return baseClassName;
}

function approvalActionButtonClassName(job: ReportJobRecord): string {
  return `primary-button compact-action-button approval-action-button approval-action-button-${approvalWorkflowKind(job)}`;
}

function approvalCompleteLabel(job: ReportJobRecord): string {
  const normalized = rawApprovalStatus(job);
  if (normalized.includes('DECLINED') || normalized.includes('DENIED') || normalized.includes('REJECTED')) return 'Declined';
  if (normalized.includes('FAIL') || normalized.includes('ERROR')) return 'Failed';
  if (normalized.includes('PENDING') || normalized.includes('REQUESTED')) return 'Pending';
  return 'Approved';
}


function approvalActionKey(job: ReportJobRecord): string {
  return [
    jobString(job, 'approvalType'),
    jobString(job, 'approvalTitle'),
    jobString(job, 'workflowType'),
    jobString(job, 'requestedAction'),
    jobString(job, 'action'),
  ].filter(Boolean).join(' ');
}

function approvalWorkflowKind(job: ReportJobRecord): ApprovalWorkflowKind {
  const key = approvalActionKey(job).trim().toUpperCase();
  if (['RUN_CDK_DEPLOYMENT', 'RUN_CLIENT_BASELINE_DEPLOY', 'RUN_DEPLOY_CLIENT_BASELINE'].some((token) => key.includes(token)) || /SOFTWARE[_\s-]*UPDATE|CDK[_\s-]*DEPLOY|CLIENT[_\s-]*BASELINE[_\s-]*DEPLOY/.test(key)) {
    return 'software';
  }
  if (['RUN_EVIDENCE_PACKAGE', 'RUN_COLLECT_EVIDENCE', 'RUN_EVIDENCE_ONLY'].some((token) => key.includes(token)) || /EVIDENCE[_\s-]*(PACKAGE|ONLY|COLLECTION)|COLLECT[_\s-]*EVIDENCE/.test(key)) {
    return 'evidence';
  }
  if (['RUN_EVIDENCE_WORKFLOW', 'RUN_TECHNICAL_BASELINE'].some((token) => key.includes(token)) || /FULL[_\s-]*(WORKFLOW|READINESS)|TECHNICAL[_\s-]*BASELINE|READINESS[_\s-]*REFRESH/.test(key)) {
    return 'full';
  }
  return 'other';
}

function approvalTitle(job: ReportJobRecord): string {
  const kind = approvalWorkflowKind(job);
  if (kind === 'full') return 'Update & evidence';
  if (kind === 'software') return 'Software update';
  if (kind === 'evidence') return 'Evidence package';
  return friendlyLabel(jobString(job, 'approvalTitle') || jobString(job, 'workflowType') || jobString(job, 'requestedAction') || 'Approval request');
}

function approvalTypePillClassName(job: ReportJobRecord): string {
  return `approval-type-pill approval-type-pill-${approvalWorkflowKind(job)}`;
}

function approvalStatus(job: ReportJobRecord): string {
  return approvalStatusLabel(
    approvalWorkflowKind(job),
    jobString(job, 'approvalStatus')
      || jobString(job, 'orchestrationStatus')
      || jobString(job, 'displayStatus')
      || jobString(job, 'status')
      || 'Pending approval',
  );
}

function approvalDecisionLabels(job: ReportJobRecord): ApprovalDecisionLabelSet {
  const actionKey = approvalActionKey(job);
  const mapping = APPROVAL_DECISION_LABEL_MAPPINGS.find((item) => item.pattern.test(actionKey));
  return mapping?.labels ?? DEFAULT_APPROVAL_DECISION_LABELS;
}

function approvalRequestedAt(job: ReportJobRecord): string {
  return jobString(job, 'approvalRequestedAt') || jobString(job, 'createdAt') || '—';
}

function approvalActorName(value: string, fallback: string): string {
  return displayNameForIdentity({ name: value, email: value }, fallback);
}

function approvalRequestedBy(job: ReportJobRecord): string {
  const requestedBy = jobString(job, 'approvalRequestedBy') || jobString(job, 'requestedBy');
  return approvalActorName(requestedBy, 'SURELINC');
}

function approvalDecidedAt(job: ReportJobRecord): string {
  return jobString(job, 'approvalDecidedAt') || '—';
}

function approvalDecidedBy(job: ReportJobRecord): string {
  const decidedBy = jobString(job, 'approvalDecidedBy');
  return decidedBy ? approvalActorName(decidedBy, 'Authorized user') : '—';
}

function rawApprovalStatus(job: ReportJobRecord): string {
  return (
    jobString(job, 'approvalStatus')
    || jobString(job, 'orchestrationStatus')
    || jobString(job, 'status')
    || ''
  ).trim().toUpperCase();
}

function canApprove(job: ReportJobRecord): boolean {
  const status = rawApprovalStatus(job);
  return [
    'PENDING',
    'PENDING_CLIENT_APPROVAL',
    'CLIENT_APPROVAL_REQUESTED',
  ].includes(status);
}

function detailRow(label: string, value?: string | number | null) {
  return (
    <div className="detail-row" key={label}>
      <span>{label}</span>
      <strong>{valueOrDash(value)}</strong>
    </div>
  );
}

export default function ApprovalsPage() {
  const { accountPath } = useClientAccount();
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [data, setData] = useState<JobsResponse | null>(null);
  const [metadata, setMetadata] = useState<GridMetadata | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState<ReportJobRecord | null>(null);
  const [actionStatus, setActionStatus] = useState<ApprovalActionStatus>({ kind: 'idle' });
  const [evidenceWorkflowAccess, setEvidenceWorkflowAccess] = useState<EvidenceWorkflowAccess | null>(null);
  const [evidenceWorkflowAccessLoading, setEvidenceWorkflowAccessLoading] = useState(true);
  const [evidenceWorkflowRequestStatus, setEvidenceWorkflowRequestStatus] = useState<ApprovalActionStatus>({ kind: 'idle' });
  const [filters, setFilters] = useState<ApprovalSearchState>({
    search: '',
    searchMode: 'contains',
    status: '',
    cmmcLevel: '',
    limit: DEFAULT_GRID_LIMIT,
  });

  async function loadEvidenceWorkflowAccess() {
    setEvidenceWorkflowAccessLoading(true);
    try {
      const response = await api.get<EvidenceWorkflowAccess>('/client/evidence-workflow');
      setEvidenceWorkflowAccess(response);
    } catch (err) {
      setEvidenceWorkflowAccess({
        enabled: false,
        canRequest: false,
        message: err instanceof Error ? err.message : 'Unable to read evidence workflow access.',
      });
    } finally {
      setEvidenceWorkflowAccessLoading(false);
    }
  }

  async function requestEvidenceWorkflow() {
    const confirmed = window.confirm(
      'Request evidence collection for the selected AWS account? The workflow will create a pending approval and collection will not begin until an authorized account administrator approves it.',
    );
    if (!confirmed) return;

    setEvidenceWorkflowRequestStatus({ kind: 'loading', message: 'Requesting evidence collection...' });
    try {
      const response = await api.post<EvidenceWorkflowRequestResponse>('/client/evidence-workflow', {
        reason: 'Client account administrator requested evidence collection from the Approvals page.',
      });
      setEvidenceWorkflowRequestStatus({
        kind: 'success',
        message: response.message || 'Evidence collection requested. Approval is still required before collection begins.',
      });
      await loadApprovals(filters, 1);
      setPage(1);
      window.setTimeout(() => {
        void loadApprovals(filters, 1);
      }, 2000);
    } catch (err) {
      setEvidenceWorkflowRequestStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Unable to request evidence collection.',
      });
    }
  }

  async function loadApprovals(nextFilters = filters, pageNumber = page) {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<JobsResponse>('/approvals', {
        query: {
          limit: nextFilters.limit,
          page: pageNumber,
          search: nextFilters.search || undefined,
          searchMode: nextFilters.searchMode,
          status: nextFilters.status || undefined,
          cmmcLevel: nextFilters.cmmcLevel || undefined,
        },
      });
      setData(response);
      setMetadata(metadataFromResponse(response));
      setPage(response.page ?? pageNumber);
      if (jobId) {
        const matching = response.jobs?.find((job: ReportJobRecord) => job.jobId === jobId) ?? null;
        setSelectedApproval(matching);
      } else {
        setSelectedApproval(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load approval requests.');
    } finally {
      setLoading(false);
    }
  }

  async function loadApprovalByJobId(targetJobId: string) {
    setLoading(true);
    setError(null);
    try {
      const logicalJobId = logicalApprovalJobId(targetJobId);
      const response = await api.get<{ approval: ReportJobRecord }>(`/approvals/${encodeURIComponent(logicalJobId)}`);
      setSelectedApproval(response.approval);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load approval details.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (jobId) {
      void loadApprovalByJobId(jobId);
    }
    void loadApprovals({ search: '', searchMode: 'contains', status: '', cmmcLevel: '', limit: DEFAULT_GRID_LIMIT }, 1);
    void loadEvidenceWorkflowAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  async function submitDecision(targetJob: ReportJobRecord, decision: 'approve' | 'decline') {
    const targetJobId = logicalApprovalJobId(targetJob.jobId);
    if (!targetJobId) {
      setActionStatus({ kind: 'error', message: 'Approval request is missing a job ID.' });
      return;
    }

    const decisionLabels = approvalDecisionLabels(targetJob);
    const confirmed = decision === 'approve'
      ? window.confirm(decisionLabels.approveConfirm)
      : window.confirm(decisionLabels.declineConfirm);
    if (!confirmed) return;

    setActionStatus({ kind: 'loading', message: decision === 'approve' ? decisionLabels.approving : decisionLabels.declining });
    try {
      const response = await api.post<{ approval: ReportJobRecord; message?: string }>(`/approvals/${encodeURIComponent(targetJobId)}/${decision}`, {});
      setSelectedApproval(response.approval);

      // Use client-portal labels as the source of truth for decision feedback.
      // The backend stores generic approval status values for workflow routing,
      // so its response message may say "evidence approved" even when the
      // approved request is a software update. Keep the user-facing toast
      // aligned to the request type shown in the grid/detail view.
      setActionStatus({ kind: 'success', message: decision === 'approve' ? decisionLabels.approved : decisionLabels.declined });
      await loadApprovals(filters, page);
    } catch (err) {
      setActionStatus({ kind: 'error', message: err instanceof Error ? err.message : 'Unable to submit approval decision.' });
    }
  }

  const approvals = data?.jobs ?? [];
  const selectedApprovalDecisionLabels = selectedApproval ? approvalDecisionLabels(selectedApproval) : DEFAULT_APPROVAL_DECISION_LABELS;

  return (
    <div className="page-stack">
      <div className="page-heading">
        <h2>Approvals</h2>
        <p>Review pending SURELINC CMMC workflow requests before evidence generation begins.</p>
      </div>

      <PackageGenerationBanner />

      <article className="card evidence-workflow-request-card">
        <div className="section-title-row">
          <div>
            <h3>Client-started evidence collection</h3>
            <p className="muted-text">
              Request a new evidence-only workflow for the selected AWS account. Every request still creates a pending approval and collection does not begin until an authorized account administrator approves it.
            </p>
          </div>
          {evidenceWorkflowAccess?.enabled && evidenceWorkflowAccess.canRequest && (
            <button
              className="success-button evidence-workflow-request-button"
              type="button"
              disabled={evidenceWorkflowAccessLoading || evidenceWorkflowRequestStatus.kind === 'loading' || evidenceWorkflowRequestStatus.kind === 'success'}
              onClick={() => void requestEvidenceWorkflow()}
            >
              {evidenceWorkflowRequestStatus.kind === 'loading' ? 'Requesting…' : evidenceWorkflowRequestStatus.kind === 'success' ? 'Request submitted' : 'Start evidence collection'}
            </button>
          )}
        </div>
        {evidenceWorkflowAccessLoading && <p className="muted-text">Checking account feature access…</p>}
        {!evidenceWorkflowAccessLoading && evidenceWorkflowAccess && !evidenceWorkflowAccess.enabled && (
          <div className="notice notice-warning">
            {evidenceWorkflowAccess.message || 'This paid feature is disabled for the selected AWS account. A SURELINC administrator must enable Client-started Evidence Collection in the account’s Paid features section.'}
          </div>
        )}
        {!evidenceWorkflowAccessLoading && evidenceWorkflowAccess?.enabled && !evidenceWorkflowAccess.canRequest && (
          <p className="muted-text">
            {evidenceWorkflowAccess.message || 'Only an active account administrator can request evidence collection.'}
          </p>
        )}
        {evidenceWorkflowRequestStatus.kind !== 'idle' && (
          <p className={evidenceWorkflowRequestStatus.kind === 'error' ? 'error-text' : evidenceWorkflowRequestStatus.kind === 'success' ? 'success-text' : 'muted-text'}>
            {evidenceWorkflowRequestStatus.message}
          </p>
        )}
      </article>

      <article className="card">
        <div className="section-title-row">
          <div>
            <h3>Pending approvals</h3>
            <p className="muted-text">Latest approval requests are shown first. Select a row to view details or approve directly from the grid.</p>
          </div>
          <button className="secondary-button" type="button" onClick={() => void loadApprovals()}>
            Refresh
          </button>
        </div>

        <form
          className="filter-grid"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            void loadApprovals(filters, 1);
          }}
        >
          <label>
            Search
            <input
              value={filters.search}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              placeholder="Approval, job, status, requester, or level"
            />
          </label>
          <label>
            Match type
            <select value={filters.searchMode} onChange={(event) => setFilters((current) => ({ ...current, searchMode: event.target.value as SearchMode }))}>
              <option value="contains">Contains</option>
              <option value="exact">Exact word</option>
              <option value="beginsWith">Begins with</option>
            </select>
          </label>
          <label>
            Status
            <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
              {STATUS_OPTIONS.map((status) => (
                <option key={status || 'all'} value={status}>{friendlyOptionLabel(status, 'All statuses')}</option>
              ))}
            </select>
          </label>
          <label>
            CMMC Level
            <select value={filters.cmmcLevel} onChange={(event) => setFilters((current) => ({ ...current, cmmcLevel: event.target.value }))}>
              {CMMC_LEVEL_OPTIONS.map((level) => (
                <option key={level || 'all'} value={level}>{level || 'All levels'}</option>
              ))}
            </select>
          </label>
          <label>
            Limit
            <select value={filters.limit} onChange={(event) => setFilters((current) => ({ ...current, limit: Number(event.target.value) }))}>
              {GRID_LIMIT_OPTIONS.map((limit) => (
                <option key={limit} value={limit}>{limit}</option>
              ))}
            </select>
          </label>
          <div className="form-actions filter-actions">
            <button className="primary-button" type="submit">Search</button>
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                const cleared: ApprovalSearchState = { search: '', searchMode: 'contains', status: '', cmmcLevel: '', limit: DEFAULT_GRID_LIMIT };
                setFilters(cleared);
                setPage(1);
                void loadApprovals(cleared, 1);
              }}
            >
              Clear
            </button>
          </div>
        </form>

        {metadata && !loading && !error && (
          <div className="grid-header-row compact-grid-header">
            <span className="result-count">Showing {approvals.length} of {metadata.matchedCount ?? approvals.length} matched · Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>
            <div className="pagination-row">
              <button className="secondary-button" type="button" disabled={!metadata.hasPreviousPage || loading} onClick={() => void loadApprovals(filters, Math.max(1, page - 1))}>Previous</button>
              <button className="secondary-button" type="button" disabled={!metadata.hasNextPage || loading} onClick={() => void loadApprovals(filters, page + 1)}>Next</button>
            </div>
          </div>
        )}

        {loading && <p>Loading approval requests...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && approvals.length === 0 && <p>No approval requests are available for this client.</p>}
        {!loading && !error && approvals.length > 0 && (
          <div className="table-scroll">
            <table className="data-table interactive-table approvals-table">
              <thead>
                <tr>
                  <th className="approval-job-cell">Job</th>
                  <th className="approval-title-cell">Approval</th>
                  <th className="approval-status-cell">Status</th>
                  <th className="approval-action-cell">Next action</th>
                  <th className="approval-level-cell">CMMC</th>
                  <th className="approval-requester-cell">Requested by</th>
                  <th className="approval-date-cell">Requested</th>
                  <th className="approval-date-cell">Decision</th>
                  <th className="approval-date-cell">Updated</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((approval: ReportJobRecord, index: number) => {
                  const rowKey = `${approval.clientId || 'client'}-${approval.jobId || index}`;
                  const selected = selectedApproval?.jobId === approval.jobId;
                  const decisionLabels = approvalDecisionLabels(approval);
                  return (
                    <tr
                      key={rowKey}
                      className={selected ? 'selected-row' : undefined}
                      onClick={() => {
                        setSelectedApproval(approval);
                        if (approval.jobId) {
                          navigate(accountPath(`/approvals/${encodeURIComponent(logicalApprovalJobId(approval.jobId))}`), { replace: false });
                        }
                      }}
                    >
                      <td className="approval-job-cell"><span className="job-id-chip">{valueOrDash(approval.jobId)}</span></td>
                      <td className="approval-title-cell">
                        <span className={approvalTypePillClassName(approval)}>{approvalTitle(approval)}</span>
                      </td>
                      <td className="approval-status-cell"><span className={approvalStatusPillClassName(approval)}>{approvalStatus(approval)}</span></td>
                      <td className="approval-action-cell">
                        {canApprove(approval) ? (
                          <div className="approval-action-button-row">
                            <button
                              className={approvalActionButtonClassName(approval)}
                              type="button"
                              title={decisionLabels.approveTitle}
                              aria-label={decisionLabels.approveTitle}
                              disabled={actionStatus.kind === 'loading'}
                              onClick={(event) => {
                                event.stopPropagation();
                                void submitDecision(approval, 'approve');
                              }}
                            >
                              {decisionLabels.gridApprove}
                            </button>
                            <button
                              className="secondary-button compact-action-button approval-decline-button"
                              type="button"
                              title={decisionLabels.declineTitle}
                              aria-label={decisionLabels.declineTitle}
                              disabled={actionStatus.kind === 'loading'}
                              onClick={(event) => {
                                event.stopPropagation();
                                void submitDecision(approval, 'decline');
                              }}
                            >
                              {decisionLabels.decline}
                            </button>
                          </div>
                        ) : (
                          <span className="approval-action-complete" title="Decision recorded">{approvalCompleteLabel(approval)}</span>
                        )}
                      </td>
                      <td className="approval-level-cell">{valueOrDash(approval.cmmcLevel)}</td>
                      <td className="approval-requester-cell">{approvalRequestedBy(approval)}</td>
                      <td className="approval-date-cell">{formatDate(approvalRequestedAt(approval))}</td>
                      <td className="approval-date-cell">{formatDate(approvalDecidedAt(approval))}</td>
                      <td className="approval-date-cell">{formatDate(approval.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {actionStatus.kind !== 'idle' && (
          <p className={actionStatus.kind === 'error' ? 'error-text' : actionStatus.kind === 'success' ? 'success-text' : 'muted-text'}>
            {actionStatus.message}
          </p>
        )}
      </article>

      {selectedApproval && (
        <article className="card detail-panel">
          <div className="section-title-row">
            <div>
              <h3>Approval details</h3>
            </div>
            {canApprove(selectedApproval) ? (
              <div className="approval-detail-actions">
                <button className="primary-button" type="button" title={selectedApprovalDecisionLabels.approveTitle} disabled={actionStatus.kind === 'loading'} onClick={() => void submitDecision(selectedApproval, 'approve')}>
                  {selectedApprovalDecisionLabels.detailApprove}
                </button>
                <button className="secondary-button" type="button" title={selectedApprovalDecisionLabels.declineTitle} disabled={actionStatus.kind === 'loading'} onClick={() => void submitDecision(selectedApproval, 'decline')}>
                  {selectedApprovalDecisionLabels.decline}
                </button>
              </div>
            ) : (
              <span className="muted-text">Decision already recorded.</span>
            )}
          </div>
          <div className="details-grid">
            {detailRow('Approval', approvalTitle(selectedApproval))}
            {detailRow('Job ID', selectedApproval.jobId)}
            <div className="detail-row"><span>Status</span><strong><span className={approvalStatusPillClassName(selectedApproval)}>{approvalStatus(selectedApproval)}</span></strong></div>
            {detailRow('CMMC level', selectedApproval.cmmcLevel)}
            {detailRow('Requested by', approvalRequestedBy(selectedApproval))}
            {detailRow('Requested at', formatDate(approvalRequestedAt(selectedApproval)))}
            {detailRow('Decision by', approvalDecidedBy(selectedApproval))}
            {detailRow('Decision at', formatDate(approvalDecidedAt(selectedApproval)))}
            {detailRow('Created', formatDate(selectedApproval.createdAt))}
            {detailRow('Updated', formatDate(selectedApproval.updatedAt))}
          </div>
        </article>
      )}
    </div>
  );
}
